import { Link } from '@tanstack/react-router'
import type { PitchSessionData, PitchSessionStatus } from '@workspace/api-backend'
import { formatDate } from '@workspace/ui/lib/utils'
import { CheckCircle, Loader2, Play, Trophy, Video, XCircle } from 'lucide-react'
import { extractEvaluationData } from '../utils/scores'
import { ScoreBadge } from './score-badge'

export const statusLabel: Record<PitchSessionStatus, string> = {
  PENDING: 'Processing',
  ACTIVE: 'Ready to Join',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  INCOMPLETE: 'Incomplete',
}

const defaultStatusDescriptions: Partial<Record<PitchSessionStatus, string>> = {
  PENDING: 'Pitch session is being set up',
  ACTIVE: 'Session is ready',
  IN_PROGRESS: 'Pitch session is live',
  FAILED: 'Something went wrong with this session',
  INCOMPLETE: 'Session ended before completion',
}

const statusTimelineIcon: Record<PitchSessionStatus, React.ReactNode> = {
  PENDING: <Loader2 className="size-5 text-amber-500 animate-spin" />,
  ACTIVE: <Video className="size-5 text-amber-500" />,
  IN_PROGRESS: <Play className="size-5 text-blue-500" />,
  COMPLETED: <CheckCircle className="size-5 text-emerald-500" />,
  FAILED: <XCircle className="size-5 text-red-500" />,
  INCOMPLETE: <XCircle className="size-5 text-red-500" />,
}

export function formatDuration(startedAt: string | null, finishedAt: string | null): string | null {
  if (!startedAt || !finishedAt) return null
  const start = new Date(startedAt).getTime()
  const end = new Date(finishedAt).getTime()
  const durationMs = end - start
  if (durationMs < 0) return null

  const minutes = Math.floor(durationMs / 60000)
  const seconds = Math.floor((durationMs % 60000) / 1000)

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`
  }
  return `${seconds}s`
}

interface PitchTimelineProps {
  pitches: PitchSessionData[]
  statusDescriptions?: Partial<Record<PitchSessionStatus, string>>
  renderBadges?: (session: PitchSessionData) => React.ReactNode
}

export function PitchTimeline({
  pitches,
  statusDescriptions = defaultStatusDescriptions,
  renderBadges,
}: PitchTimelineProps) {
  return (
    <div className="relative space-y-3">
      {/* Continuous timeline line */}
      <div className="absolute left-[9px] top-0 bottom-0 w-px bg-border" />
      {pitches.map((session) => {
        const label = statusLabel[session.status]
        const duration = formatDuration(session.startedAt, session.finishedAt)
        const { totalScore, winnerJudge } = extractEvaluationData(session.evaluations?.[0])
        const winnerLabel = winnerJudge?.label ?? null
        return (
          <Link
            key={session.id}
            to="/pitches/$pitchId"
            params={{ pitchId: session.id }}
            className="group relative flex items-start gap-3"
          >
            {/* Timeline icon */}
            <div className="relative z-10 shrink-0 bg-background py-2">
              {statusTimelineIcon[session.status]}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 rounded-lg border border-transparent group-hover:border-border group-hover:bg-input/50 px-3 py-2 transition-colors">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-white">
                  {formatDate(session.createdAt, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
                <span className="text-xs text-muted-foreground">{duration ?? label}</span>
              </div>

              {/* Status description for non-completed pitches */}
              {statusDescriptions[session.status] && (
                <p className="text-xs text-muted-foreground mt-1">
                  {statusDescriptions[session.status]}
                </p>
              )}

              {/* Winner judge + score for completed pitches */}
              {session.status === 'COMPLETED' && totalScore && (
                <div className="flex items-center gap-3 mt-2">
                  <ScoreBadge score={totalScore} size="md" />
                  {winnerLabel && (
                    <div className="flex items-center gap-1.5">
                      <Trophy className="size-4 text-amber-400" />
                      <span className="text-sm font-semibold text-white">{winnerLabel}</span>
                    </div>
                  )}
                </div>
              )}

              {renderBadges?.(session)}
            </div>
          </Link>
        )
      })}
    </div>
  )
}
